import { type IUseFilterProps, useFilters } from "@fluidattacks/design";
import type { IOptions, IOptionsProps } from "@fluidattacks/design";
import { camelCase } from "lodash";
import type React from "react";
import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { authContext } from "context/auth";
import type { IStakeholderDataSet } from "pages/organization/members/types";
import { useFiltersStorage } from "storage/filters";

const useMembersFilters = ({
  dataset,
  setFilteredDataset,
  type,
}: Readonly<{
  dataset: IStakeholderDataSet[];
  setFilteredDataset: React.Dispatch<
    React.SetStateAction<IStakeholderDataSet[]>
  >;
  type: "organization" | "group";
}>): IUseFilterProps<IStakeholderDataSet> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();
  const { userEmail } = useContext(authContext);
  const isInternalUser = useMemo(
    (): boolean => userEmail.endsWith("@fluidattacks.com"),
    [userEmail],
  );

  const groupRoles = useMemo(
    (): string[] => [
      ...(isInternalUser
        ? [
            "admin",
            "customer_manager",
            "hacker",
            "reattacker",
            "reviewer",
            "resourcer",
          ]
        : []),
      "group_manager",
      "vulnerability_manager",
      "user",
    ],
    [isInternalUser],
  );

  const organizationRoles = useMemo(
    (): string[] => [
      ...(isInternalUser ? ["admin", "customer_manager", "resourcer"] : []),
      "organization_manager",
      "user",
    ],
    [isInternalUser],
  );

  const options = useMemo(
    (): IOptionsProps<IStakeholderDataSet>[] => [
      {
        filterOptions: [
          {
            key: "role",
            label: t("searchFindings.usersTable.userRole"),
            options: (type === "group" ? groupRoles : organizationRoles).map(
              (role): IOptions => ({
                label: t(`userModal.roles.${camelCase(role)}`),
                value: role,
              }),
            ),
            type: "checkboxes",
          },
        ],
        label: t("searchFindings.usersTable.userRole"),
      },
      {
        filterOptions: [
          {
            key: "invitationState",
            label: t("searchFindings.usersTable.invitationState"),
            options: [
              { label: "Pending", value: "PENDING" },
              { label: "Registered", value: "REGISTERED" },
              { label: "Unregistered", value: "UNREGISTERED" },
            ],
            type: "checkboxes",
          },
        ],
        label: t("searchFindings.usersTable.invitationState"),
      },
    ],
    [t, groupRoles, organizationRoles, type],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("members") as string | null;
  }, [getValues]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("members", value);
    },
    [setValues],
  );

  return useFilters({
    dataset,
    getValues: handleGetValues,
    options,
    setFilteredDataset,
    setValues: handleSetValues,
  });
};

export { useMembersFilters };
